// =============================================================================
// KioskHome — the crew tablet's home (mig 410, 30 Sep 2026)
// =============================================================================
// James: "the field guys need a stand alone iPad or tablet with a crew level
// login to ops that's logged in all the time for that crew, it's all view
// only and the home page should be the site folders … seeing inspection logs
// for auditors and the like but no employee linked so none of the personal
// stuff and maybe an option to open the live board. Crew schedule should be
// there, the program for the site it's on, documents."
//
// What's here, in the order a crew wants it in the ute:
//   1. THE JOBS — every project the tablet's crew is booked on (current,
//      upcoming, ended within 14 days), each a big tile straight into the
//      SITE FOLDER (map, emergency, documents, HOP, diary, SDS; the Schedule
//      tab on the job page carries the program).
//   2. CREW SCHEDULE — the crew's booked windows and the office's confirmed
//      bookings on those jobs for the next four weeks.
//   3. THE REGISTERS — Inspections (the auditor's view), MOPs library, SDS
//      register, and the Live Board full screen.
// Nothing personal — no timesheets, leave, licences, receipts, policies. The
// database refuses every write from this login (app.block_kiosk_writes).
// =============================================================================

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FolderOpen, ClipboardList, BookOpen, FlaskConical, MonitorPlay, CalendarRange, MapPin, Truck, ChevronRight, Tablet, Siren } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Spinner } from "@/components/ui";

type KioskProject = {
  project_id: string; project_code: string; project_name: string; project_status: string;
  site_id: string | null; site_name: string | null; suburb: string | null; state: string | null;
  crew_id: string; crew_name: string; assigned_from: string | null; assigned_to: string | null;
};

function fmtDay(iso?: string | null) { return iso ? new Date(iso).toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short" }) : ""; }
function fmtTime(iso?: string | null) { return iso ? new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }) : ""; }
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export default function KioskHome() {
  const user = useRequireAuth();

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["kiosk_projects", user?.user_id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_kiosk_crew_projects").select("*").order("assigned_from", { ascending: false });
      if (error) throw error;
      return (data ?? []) as KioskProject[];
    },
  });
  const projectIds = jobs.map(j => j.project_id);

  // The office's confirmed bookings on these jobs, next four weeks — same
  // source as the site folder's Bookings page (tentative/cancelled never show).
  const { data: bookings = [] } = useQuery({
    queryKey: ["kiosk_bookings", projectIds.join(",")],
    enabled: projectIds.length > 0,
    queryFn: async () => {
      const to = new Date(Date.now() + 28 * 86400e3).toISOString();
      const from = new Date(Date.now() - 86400e3).toISOString();
      const { data, error } = await supabase.from("v_upcoming_bookings").select("*")
        .in("project_id", projectIds).eq("status", "confirmed")
        .gte("scheduled_start", from).lte("scheduled_start", to)
        .order("scheduled_start").limit(40);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  if (!user) return null;
  const today = todayKey();
  const live = jobs.filter(j => (!j.assigned_to || j.assigned_to >= today));
  const recent = jobs.filter(j => j.assigned_to && j.assigned_to < today);
  const crewName = jobs[0]?.crew_name ?? null;

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto pb-24">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[11px] tracking-wider2 uppercase font-mono text-text-muted flex items-center gap-2"><Tablet size={12}/> Crew tablet · view only</div>
          <h1 className="text-[24px] font-semibold tracking-tight">{crewName ?? user.device_label ?? "Crew tablet"}{crewName && user.device_label ? <span className="text-text-muted font-normal text-[16px]"> · {user.device_label}</span> : null}</h1>
        </div>
        <a href="/display" className="inline-flex items-center gap-2 rounded-xl bg-ink-raised border border-line px-4 py-2.5 text-[13px] font-medium text-text hover:border-accent/50"><MonitorPlay size={16} className="text-accent"/> Live Board</a>
      </div>

      {/* the jobs */}
      <div className="text-[11px] uppercase tracking-wider text-text-muted px-1 mb-2 flex items-center gap-3"><span className="h-1 w-8 bg-accent rounded-full"/> Site folders</div>
      {isLoading ? <div className="py-10 flex justify-center"><Spinner size={20}/></div>
        : jobs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line p-6 text-[13px] text-text-muted">
            {user.crew_id ? "This crew isn't booked on a job right now. When the office books the crew, its site folders appear here." : "This tablet isn't tied to a crew yet — ask the office (Admin → Crew tablets)."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...live, ...recent].map(j => (
              <Link key={j.project_id} to={`/projects/${j.project_id}/site-folder`}
                    className={`rounded-2xl border border-line bg-ink-raised p-4 hover:border-accent/50 transition-colors block ${j.assigned_to && j.assigned_to < today ? "opacity-70" : ""}`}>
                <div className="flex items-start gap-3">
                  <span className="w-11 h-11 rounded-xl bg-accent text-black flex items-center justify-center shrink-0"><FolderOpen size={22}/></span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-mono text-[11px] text-accent">{j.project_code}</span>
                    <span className="block text-[16px] font-semibold text-text leading-tight">{j.site_name ?? j.project_name}</span>
                    <span className="block text-[12px] text-text-muted truncate">{[j.suburb, j.state].filter(Boolean).join(", ") || j.project_name}</span>
                    <span className="block text-[11px] text-text-muted mt-1">
                      {j.assigned_to && j.assigned_to < today ? `finished ${fmtDay(j.assigned_to)}` : j.assigned_from && j.assigned_from > today ? `from ${fmtDay(j.assigned_from)}` : "on site now"}
                    </span>
                  </span>
                  <ChevronRight size={18} className="text-text-muted self-center"/>
                </div>
              </Link>
            ))}
          </div>
        )}

      {/* crew schedule */}
      <div className="text-[11px] uppercase tracking-wider text-text-muted px-1 mt-6 mb-2 flex items-center gap-3"><span className="h-1 w-8 bg-accent rounded-full"/> Crew schedule — next 4 weeks</div>
      <div className="rounded-2xl border border-line bg-ink-raised divide-y divide-line/50">
        {live.map(j => (
          <div key={`w-${j.project_id}`} className="px-4 py-3 flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-accent/15 text-accent flex items-center justify-center"><MapPin size={16}/></span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-medium text-text">{j.site_name ?? j.project_name}</span>
              <span className="block text-[11px] text-text-muted">{j.assigned_from ? `${fmtDay(j.assigned_from)} → ` : ""}{j.assigned_to ? fmtDay(j.assigned_to) : "open-ended"} · {j.project_code}</span>
            </span>
          </div>
        ))}
        {bookings.map(b => (
          <div key={b.booking_id} className="px-4 py-3 flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-info/15 text-info flex items-center justify-center"><Truck size={16}/></span>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-medium text-text">{b.title}</span>
              <span className="block text-[11px] text-text-muted">{fmtDay(b.scheduled_start)} {fmtTime(b.scheduled_start)}{b.scheduled_end ? `–${fmtTime(b.scheduled_end)}` : ""} · {b.site_name ?? b.project_name}{b.supplier_name ? ` · ${b.supplier_name}` : b.contact_employee_name ? ` · ${b.contact_employee_name}` : ""}</span>
            </span>
          </div>
        ))}
        {live.length === 0 && bookings.length === 0 && <div className="px-4 py-4 text-[12px] text-text-muted">Nothing booked for this crew in the next four weeks.</div>}
      </div>

      {/* registers */}
      <div className="text-[11px] uppercase tracking-wider text-text-muted px-1 mt-6 mb-2 flex items-center gap-3"><span className="h-1 w-8 bg-accent rounded-full"/> Registers</div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {([
          ["/inspections", "Inspections", "Rigging tags, test & tag — the auditor's view", ClipboardList, "#0F766E"],
          ["/mops", "MOPs library", "Carrier and supplier work instructions", BookOpen, "#7C3AED"],
          ["/sds", "SDS register", "Every product's safety data sheet", FlaskConical, "#B45309"],
          ["/alerts", "HSE alerts", "Current client and Coretel safety alerts", Siren, "#B91C1C"],
          ["/display", "Live Board", "The company board, full screen", MonitorPlay, "#2563EB"],
        ] as const).map(([to, title, sub, Icon, colour]) => (
          to === "/display"
            ? <a key={to} href={to} className="text-left rounded-2xl border border-line bg-ink-raised p-4 hover:border-accent/50 transition-colors block">
                <span className="w-10 h-10 rounded-xl text-white flex items-center justify-center mb-2" style={{ background: colour }}><Icon size={20}/></span>
                <span className="block text-[14px] font-semibold text-text">{title}</span><span className="block text-[11px] text-text-muted">{sub}</span>
              </a>
            : <Link key={to} to={to} className="text-left rounded-2xl border border-line bg-ink-raised p-4 hover:border-accent/50 transition-colors block">
                <span className="w-10 h-10 rounded-xl text-white flex items-center justify-center mb-2" style={{ background: colour }}><Icon size={20}/></span>
                <span className="block text-[14px] font-semibold text-text">{title}</span><span className="block text-[11px] text-text-muted">{sub}</span>
              </Link>
        ))}
      </div>
      <div className="text-[11px] text-text-muted mt-6 flex items-center gap-2"><CalendarRange size={12}/> This tablet is view only. Start cards, SWMS acceptance, diary entries and photos are done from your own phone login.</div>
    </div>
  );
}
